import { zodResolver } from '@hookform/resolvers/zod'
import { BookOpen } from 'lucide-react'
import { useId } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { applyServerErrors } from '@/components/forms/applyServerErrors'
import { FormField } from '@/components/forms/FormField'
import { toast } from '@/components/feedback/toast-store'
import { Checkbox } from '@/components/ui/Checkbox'
import { Input } from '@/components/ui/Input'
import { RadioGroup } from '@/components/ui/RadioGroup'
import { useSaveSubject, type SubjectInput } from '../api'
import type { Subject, SubjectType } from '../types'
import { FormDialog } from './FormDialog'

const SUBJECT_TYPES: { value: SubjectType; label: string; description: string }[] = [
  { value: 'theory', label: 'Theory', description: 'Classroom lessons' },
  { value: 'practical', label: 'Practical', description: 'Lab, studio or field' },
  { value: 'both', label: 'Both', description: 'Theory and practical' },
]

const PRESETS = [
  '#4f46e5',
  '#0ea5e9',
  '#10b981',
  '#22c55e',
  '#f59e0b',
  '#f97316',
  '#ef4444',
  '#ec4899',
  '#a855f7',
  '#64748b',
]

const subjectSchema = z.object({
  name: z.string().trim().min(1, 'Enter the subject name.').max(100, 'Use at most 100 characters.'),
  code: z
    .string()
    .trim()
    .max(20, 'Use at most 20 characters.')
    .regex(/^[A-Za-z0-9_-]*$/, 'Use letters, digits, - and _ only.')
    .transform((v) => (v === '' ? null : v.toUpperCase())),
  type: z.enum(['theory', 'practical', 'both'], { error: 'Choose a type.' }),
  color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, 'Use a #rrggbb colour.')
    .nullable(),
})

type SubjectFormInput = z.input<typeof subjectSchema>
const FIELDS = ['name', 'code', 'type', 'color'] as const
const FORM_ID = 'subject-form'

interface SubjectDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  subject?: Subject | null
}

export function SubjectDialog({ open, onOpenChange, subject }: SubjectDialogProps) {
  const { t } = useTranslation('academic')
  const save = useSaveSubject()
  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      icon={<BookOpen />}
      title={subject ? t('Edit {{name}}', { name: subject.name }) : t('New subject')}
      description={t("Subjects are added to each class's curriculum.")}
      formId={FORM_ID}
      submitLabel={subject ? t('Save changes') : t('Create subject')}
      pending={save.isPending}
    >
      <SubjectForm
        defaults={{
          name: subject?.name ?? '',
          code: subject?.code ?? '',
          type: subject?.type ?? 'theory',
          color: subject ? subject.color : (PRESETS[0] ?? null),
        }}
        onSubmit={async (values) => {
          const saved = await save.mutateAsync({ ...values, id: subject?.id })
          toast.success(subject ? t('Subject updated') : t('Subject created'), saved.name)
          onOpenChange(false)
        }}
      />
    </FormDialog>
  )
}

function SubjectForm({
  defaults,
  onSubmit,
}: {
  defaults: SubjectFormInput
  onSubmit: (values: SubjectInput) => Promise<void>
}) {
  const { t } = useTranslation('academic')
  const noColourId = useId()
  const {
    register,
    control,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<SubjectFormInput, unknown, SubjectInput>({
    resolver: zodResolver(subjectSchema),
    defaultValues: defaults,
  })

  const submit = handleSubmit(async (values) => {
    try {
      await onSubmit(values)
    } catch (e) {
      applyServerErrors(e, setError, FIELDS)
    }
  })

  return (
    <form
      id={FORM_ID}
      onSubmit={(e) => void submit(e)}
      noValidate
      className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_9rem]"
    >
      <FormField label={t('Name')} error={errors.name?.message} required>
        <Input placeholder={t('Mathematics')} autoComplete="off" {...register('name')} />
      </FormField>
      <FormField label={t('Code')} error={errors.code?.message} hint={t('Short label for timetables.')}>
        <Input placeholder="MATH" className="font-mono uppercase" autoComplete="off" {...register('code')} />
      </FormField>

      <fieldset className="sm:col-span-2">
        <legend className="mb-1.5 text-[0.8125rem] font-medium text-ink-soft">{t('Type')}</legend>
        <Controller
          control={control}
          name="type"
          render={({ field }) => (
            <RadioGroup
              aria-label={t('Subject type')}
              appearance="cards"
              orientation="horizontal"
              className="sm:grid-cols-3"
              value={field.value}
              onValueChange={field.onChange}
              options={SUBJECT_TYPES.map((o) => ({ ...o, label: t(o.label), description: t(o.description) }))}
            />
          )}
        />
        {errors.type ? (
          <p className="mt-1.5 text-xs font-medium text-danger-600">{errors.type.message}</p>
        ) : null}
      </fieldset>

      <Controller
        control={control}
        name="color"
        render={({ field }) => (
          <fieldset className="sm:col-span-2">
            <legend className="mb-1.5 text-[0.8125rem] font-medium text-ink-soft">{t('Colour')}</legend>
            <div className="flex flex-wrap items-center gap-3">
              <input
                type="color"
                aria-label={t('Pick a colour')}
                value={field.value ?? '#94a3b8'}
                disabled={field.value === null}
                onChange={(e) => field.onChange(e.target.value)}
                className="h-10 w-14 cursor-pointer rounded-control border border-line bg-surface p-1 disabled:cursor-not-allowed disabled:opacity-40"
              />
              <span className="font-mono text-xs text-muted">{field.value ?? t('none')}</span>
              <div className="flex flex-wrap gap-1.5" role="group" aria-label={t('Preset colours')}>
                {PRESETS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    aria-label={t('Use {{color}}', { color: c })}
                    aria-pressed={field.value === c}
                    onClick={() => field.onChange(c)}
                    className="size-6 rounded-full ring-offset-2 ring-offset-surface transition-shadow aria-pressed:ring-2 aria-pressed:ring-brand-500"
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
            </div>
            <label htmlFor={noColourId} className="mt-3 flex items-center gap-2 text-sm text-ink-soft">
              <Checkbox
                id={noColourId}
                checked={field.value === null}
                onCheckedChange={(c) => field.onChange(c === true ? null : PRESETS[0])}
              />
              {t('No colour')}
            </label>
            {errors.color ? (
              <p className="mt-1.5 text-xs font-medium text-danger-600">{errors.color.message}</p>
            ) : null}
          </fieldset>
        )}
      />
    </form>
  )
}
